/**
 * ============================================================================
 * DATE HELPER UTILITIES
 * ============================================================================
 * Pure utility functions for date formatting and parsing
 * Version: v3.10.0-CLEAR-MAP
 */

/**
 * Format date value to YYYY-MM-DD string (or with time if includeTime is true)
 * Handles multiple input formats:
 * - ISO strings
 * - Date objects  
 * - {year, month, day} objects from backend
 * @param {*} dateValue - The date value to format
 * @param {boolean} includeTime - If true, include time in format "MMM DD, YYYY h:mm AM/PM"
 */
export const formatDate = (dateValue, includeTime = false) => {
  if (!dateValue) return '';
  const debugTimezone = process.env.REACT_APP_DEBUG_TIMEZONE === 'true';
  
  // Handle string dates
  if (typeof dateValue === 'string') {
    // 🔧 FIX: Backend sends ISO strings WITHOUT 'Z' suffix
    // This causes JavaScript to parse them as LOCAL time instead of UTC
    // Add 'Z' to ISO strings that don't have a timezone indicator
    let normalizedValue = dateValue;
    if (/^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}/.test(dateValue) && 
        !dateValue.endsWith('Z') && 
        !dateValue.includes('+') && 
        !dateValue.includes('-', 10)) {
      // ISO string without timezone - assume UTC
      normalizedValue = dateValue + 'Z';
      if (debugTimezone) {
        console.log(`🔧 TIMEZONE-FIX: Added 'Z' to ISO string: ${dateValue} → ${normalizedValue}`);
      }
    }
    
    // Try to parse as date
    const d = new Date(normalizedValue);
    if (!isNaN(d.getTime())) {
      if (includeTime) {
        // Format: "Nov 4, 2025 2:30 PM" in Houston Central Time
        const formatted = d.toLocaleString('en-US', {
          month: 'short',
          day: 'numeric',
          year: 'numeric',
          hour: 'numeric',
          minute: '2-digit',
          hour12: true,
          timeZone: 'America/Chicago' // Houston Central Time (handles CST/CDT automatically)
        });
        if (debugTimezone) {
          console.log(`🕐 Timezone conversion: ${dateValue} → ${formatted} (Central Time)`);
        }
        return formatted;
      }
      // Already in YYYY-MM-DD format and no time requested
      if (/^\d{4}-\d{2}-\d{2}$/.test(dateValue) && !includeTime) {
        return dateValue;
      }
      return d.toISOString().split('T')[0];
    }
    return dateValue;
  }
  
  // Handle Date objects
  if (dateValue instanceof Date) {
    if (!isNaN(dateValue.getTime())) {
      if (includeTime) {
        return dateValue.toLocaleString('en-US', {
          month: 'short',
          day: 'numeric',
          year: 'numeric',
          hour: 'numeric',
          minute: '2-digit',
          hour12: true,
          timeZone: 'America/Chicago' // Houston Central Time (handles CST/CDT automatically)
        });
      }
      return dateValue.toISOString().split('T')[0];
    }
  }
  
  // Handle {year, month, day} objects from backend
  if (typeof dateValue === 'object' && dateValue !== null) {
    const { year, month, day } = dateValue;
    if (year && month && day) {
      return `${year}-${String(month).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
    }
  }
  
  return '';
};

